<template>
    <!--
        To prevent issues that occur from a user not being initialized we use a
        loading layer as a guard to ensure that a user does not progress into the
        application while they are authorizing or are authorized but do not yet
        have scopes loaded and you're not on the user not found page as if so me would be null
        -->
    <hox-loading-layer v-if="isAuthorizing || (isAuthorized && !me && !isUserNotFoundRoute)" size="large" />
    <template v-else>
        <notifications-drawer />
        <component :is="currentTheme" />
        <hox-modal v-if="mediaLibraryModalIsVisible" fullscreen @close="hideMediaLibraryModal">
            <template #header>Content Library</template>
            <media-library />
            <template #footer>
                <portal-target name="media-library-modal-footer" />
            </template>
        </hox-modal>
        <hox-modal v-if="adblockModalIsVisible" @close="hideAdblockModal">
            <template #header>Ad Blocker detected!</template>
            <div>
                You will need to disable your Ad Blocker on *.hoxton.co and *.hogarth.ai to view the banners on this
                platform.
            </div>
            <template #footer>
                <div class="remember-checkbox">
                    <Checkbox :model-value="false" @onChange="rememberAdblockSetting">Don't show this again</Checkbox>
                </div>
                <Button @click="hideAdblockModal">Close</Button>
            </template>
        </hox-modal>
    </template>
    <modal-portal />
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { LocationQueryValue, RouteLocationNormalizedLoaded } from "vue-router";
import { mapMutations } from "vuex";
import { mapStores } from "pinia";

import { GA4Tracker, initGTM, type OpenOSContext } from "ic-analytics";
import { USER_ACTIONS } from "shared-utils/enums/user";

import bus from "@/bus";

import MediaLibrary from "@/components/MediaLibrary.vue";
import HoxModal from "@/components/Modal/Modal/Modal.vue";
import ModalPortal from "@/components/Modal/Modal/ModalPortal.vue";
import NotificationsDrawer from "@/components/Notifications/NotificationsDrawer.vue";

import ThemeCampaignLight from "@/ThemeCampaignLight.vue";
import ThemeDefault from "@/ThemeDefault.vue";
import ThemeLogin from "@/ThemeLogin.vue";
import ThemeTemplatesLight from "@/ThemeTemplatesLight.vue";

import meQuery from "@/apollo/queries/Me.gql";
import rolesQuery from "@/apollo/queries/Roles.gql";

import { AuthMutation, type Me } from "@/store/modules/auth";
import { MediaLibraryAction } from "@/store/modules/mediaLibrary";
import { RolesAction, type Role } from "@/store/modules/roles";
import { UiAction } from "@/store/modules/ui";
import { useCommonStore } from "@/store/pinia/common";
import { AppRouteNames } from "@/router/constants";

import {
    configureAmplify,
    getIsAuthorized,
    getOauthConfig,
    onSessionLost,
    providerLogin,
    userCan,
    type OauthConfig
} from "@/services/Auth";
import { getWppContext } from "@/services/wppAuth";

import { getLandingRoute } from "@/router";
import type { Err } from "@/types/types";

const AdblockStorageKey = "adblockNoticeDisabled";

export default defineComponent({
    name: "App",
    components: {
        HoxModal,
        MediaLibrary,
        ModalPortal,
        NotificationsDrawer,
        ThemeCampaignLight,
        ThemeDefault,
        ThemeLogin,
        ThemeTemplatesLight
    },

    data() {
        return {
            isAuthorizing: false,
            localAdblockModalIsVisible: true
        };
    },

    computed: {
        ...mapStores(useCommonStore),

        theme() {
            return this.$store.state.ui.theme;
        },

        themeMode() {
            return this.$store.state.ui.themeMode;
        },

        activeThemeName() {
            if (this.$route.meta.forceLightTheme) {
                return "light";
            }

            return this.themeMode.theme || "light";
        },

        isAuthorized() {
            return this.$store.state.auth.isAuthorized;
        },

        me() {
            return this.$store.state.auth.me;
        },

        isWppLogin() {
            return this.$store.state.auth.isWppLogin;
        },

        wppOsContext() {
            return this.$store.state.auth.wppOsContext;
        },

        wppProjectId() {
            return this.wppOsContext?.project?.id;
        },

        ga4Tracker() {
            return GA4Tracker.getInstance();
        },

        currentTheme() {
            return `theme-${this.theme}`;
        },

        isNetworkErrorRoute() {
            return this.$route.name === AppRouteNames.NETWORK_ERROR;
        },

        isLocalDevNetworkErrorRoute() {
            return this.$route.name === AppRouteNames.LOCAL_DEV_NETWORK_ERROR;
        },

        isUserNotFoundRoute() {
            return this.$route.name === AppRouteNames.USER_NOT_FOUND;
        },

        wrapperClassName() {
            const routeName = this.$route.name as string | undefined;
            const classNames = ["app-wrapper", "wpp-theme", `wpp-theme-${this.activeThemeName}`];

            if (routeName) {
                classNames.push(`wpp-theme--${routeName.toLowerCase()}`);
            }

            return classNames.join(" ");
        },

        adblockModalIsVisible() {
            return (
                this.localAdblockModalIsVisible &&
                !(window as Window & typeof globalThis & { canRunAds: boolean }).canRunAds &&
                !localStorage.getItem(AdblockStorageKey)
            );
        },

        mediaLibraryModalIsVisible() {
            return this.$store.state.mediaLibrary.open;
        }
    },

    watch: {
        wrapperClassName() {
            this.updateAppRootClassName();
        },

        me: {
            immediate: true,
            handler(newMe) {
                if (newMe) {
                    void this.initializeGA4WithStandaloneUser();
                }
            }
        },

        $route(to: RouteLocationNormalizedLoaded, from?: RouteLocationNormalizedLoaded) {
            if (!this.ga4Tracker?.isInitialized) {
                console.warn("navigated away before GA4 Tracker initialization", { from: from?.path, to: to.path });

                return;
            }

            const currentUrl = `${window.location.origin}${to.fullPath}`;
            const previousUrl = from ? `${window.location.origin}${from.fullPath}` : "";
            const pageTitle = (to.meta.title as string | undefined) ?? document.title;

            this.ga4Tracker.trackVirtualPageView(currentUrl, pageTitle, previousUrl);
        },

        wppProjectId: {
            handler(newProjectId) {
                if (newProjectId) {
                    this.updateGA4WithProjectData();
                }
            }
        }
    },

    async created() {
        this.$snackbar.set(this.$Message);
        this.isAuthorizing = true;

        this.createGA4Tracker();
        const oauthConfig = await this.getOauthConfig();

        if (this.getQueryParam("error")) {
            // Failed OAuth (e.g. access_denied): configure for retry, stay unauthorized.
            configureAmplify(oauthConfig);
            this.setIsAuthorized(false);
        } else if (this.getQueryParam("type") === "wpp") {
            try {
                await this.wppAuthorize();
            } catch (e) {
                console.error("Failed to get WPP token", e);

                try {
                    await this.amplifyAuthorize(oauthConfig);
                } catch (err) {
                    this.$snackbar.authentication(err as Err);
                }
            }
        } else {
            try {
                await this.amplifyAuthorize(oauthConfig);
            } catch (err) {
                this.$snackbar.authentication(err as Err);
            }
        }

        const errorDescription = this.getQueryParam("error_description") as string;

        if (errorDescription) {
            if (errorDescription === "User is not assigned to the client application.; error=access_denied") {
                this.$snackbar.info(
                    'Oops! Looks like you don\'t have access. Click <a href="https://jira.uhub.biz/servicedesk/customer/portal/4408/create/24102">here</a> to request access now',
                    "",
                    {
                        closable: true,
                        duration: 0
                    }
                );
            } else {
                this.$snackbar.info("Oops! Looks like you don't have access.", errorDescription, {
                    closable: true,
                    duration: 0
                });
            }
        }

        const route = getLandingRoute(this.$route);

        this.isAuthorizing = false;

        if (route) {
            if (route.name !== this.$route.name || this.getQueryParam("code")) {
                void this.$router.push(route);
            }
        }

        const savedTheme = localStorage?.getItem("currentTheme");

        if (savedTheme) {
            this.$store.dispatch(UiAction.SetThemeMode, JSON.parse(savedTheme));
        }
    },

    mounted() {
        this.updateAppRootClassName();

        bus.$on("userNotFound", () => {
            if (!this.isUserNotFoundRoute) {
                void this.$router.push({ name: AppRouteNames.USER_NOT_FOUND });
            }
        });

        bus.$on("networkError", () => {
            if (!this.isNetworkErrorRoute) {
                void this.$router.push({ name: AppRouteNames.NETWORK_ERROR });
            }
        });

        bus.$on("localDevNetworkError", () => {
            if (!this.isLocalDevNetworkErrorRoute) {
                void this.$router.push({ name: AppRouteNames.LOCAL_DEV_NETWORK_ERROR });
            }
        });

        bus.$on("unauthorized", () => {
            // Clear stale auth flag and go to login.
            this.setIsAuthorized(false);

            if (this.$route.name !== AppRouteNames.LOGIN) {
                this.$router.push({ name: AppRouteNames.LOGIN, query: this.$route.query }).catch(() => undefined);
            }
        });

        // Drop auth as soon as the session can't be refreshed.
        onSessionLost(() => bus.$emit("unauthorized"));

        bus.$on("percentLoadEvent", load => {
            this.$Loading.update(load);

            if (load >= 100) {
                this.$Loading.finish();
            }
        });

        bus.$on("apolloLoadEvent", load => {
            this.$Loading.start();

            if (load === 0) {
                this.$Loading.finish();
            } else {
                this.$Loading.update(100 / load);
            }
        });

        bus.$on("apolloErrorEvent", err => {
            this.$Loading.error();

            // Process global errors
            if (err.statusCode && [401, 403].includes(err.statusCode)) {
                this.$snackbar.unauthorized(err);
            } else if (Array.isArray(err.graphQLErrors)) {
                this.$snackbar.graphQLErrors(err.graphQLErrors);
            }
        });
    },

    methods: {
        ...mapMutations("auth", ["setIsAuthorized", "setIsWppLogin", "setWppOsContext"]),

        updateAppRootClassName() {
            const appNode = document.getElementById("appRoot");

            if (appNode) {
                appNode.className = this.wrapperClassName;
            }

            document.documentElement.setAttribute("data-theme", this.activeThemeName);
        },

        async getOauthConfig() {
            const oauthConfig = await getOauthConfig();

            // defined in vite config, only when running on a local machine with a local backend
            if (__RUN_LOCALLY__) {
                this.commonStore.baseApiURL = "http://local-dev.hoxton.co:8080";
            } else {
                this.commonStore.baseApiURL = oauthConfig.apiURL;
            }

            return oauthConfig;
        },

        async wppAuthorize() {
            await getWppContext(this.handleWppContext);

            this.setIsWppLogin(true);
            this.setIsAuthorized(true);
        },

        async amplifyAuthorize(oauthConfig: OauthConfig) {
            const isAuthorized = await getIsAuthorized(oauthConfig);

            this.setIsAuthorized(isAuthorized);

            if (isAuthorized) {
                return;
            }

            if (this.getQueryParam("iss")) {
                await providerLogin("Okta-OIDC-IC-LandingPage");
            }
        },

        hideAdblockModal() {
            this.localAdblockModalIsVisible = false;
        },

        hideMediaLibraryModal() {
            void this.$store.dispatch(MediaLibraryAction.Close);
        },

        rememberAdblockSetting(val: boolean) {
            if (val) {
                localStorage.setItem(AdblockStorageKey, "true");
            } else {
                localStorage.removeItem(AdblockStorageKey);
            }
        },

        getQueryParam(param: string): string | LocationQueryValue[] | null {
            return this.$route.query[param] || new URLSearchParams(window.location.search).get(param);
        },

        createGA4Tracker() {
            const isInIframe = window.self !== window.top;
            const parentUrl = isInIframe ? document.referrer : "";
            const isOpenOS = parentUrl.includes("wpp.com") || parentUrl.includes("os.wpp.com");
            const isDevEnvironment =
                window.location.hostname.includes("assemble.dev") || window.location.hostname.includes("local-dev");
            const isWppContext = this.getQueryParam("type") === "wpp" || isOpenOS;

            if (!isInIframe || !isOpenOS) {
                initGTM({ gtmId: __GTM_ID__, dataLayerName: "wppopen_dataLayer" });
            }

            GA4Tracker.createInstance({
                dataLayerName: "wppopen_dataLayer",
                preventTracking: (isInIframe && isOpenOS) || isDevEnvironment
            });

            this.ga4Tracker.setContextData({
                contextType: isWppContext ? "os" : "standalone",
                tenantName: isWppContext ? "WPP Open" : undefined
            });
        },

        handleWppContext(context: OpenOSContext | undefined) {
            this.setWppOsContext(context);

            if (this.ga4Tracker?.isInitialized) {
                this.updateGA4WithProjectData();
            } else {
                void this.initializeGA4WithOpenOSContext();
            }
        },

        updateGA4WithProjectData() {
            if (!this.ga4Tracker || !this.wppOsContext) {
                return;
            }

            const updatedContext = {
                ...this.wppOsContext,
                project: {
                    ...this.wppOsContext.project,
                    name: this.wppOsContext.project.name || `Project ${this.wppProjectId}`
                }
            };

            this.ga4Tracker.setContextData({
                contextType: "os",
                project: updatedContext.project.name,
                tenantName: updatedContext.tenant.name || "WPP Open"
            });
        },

        async setGA4UserData(context: typeof this.wppOsContext) {
            if (context.userDetails) {
                await this.ga4Tracker.setUserData({
                    email: context.userDetails.email,
                    company: context.userDetails.company || "unknown",
                    countryCode: context.userDetails.countryAlpha2Code || "unknown",
                    department: context.userDetails.department || "unknown",
                    jobRole: context.userDetails.jobTitle || "unknown",
                    region: context.userDetails.geoRegion || "unknown",
                    isExternal: context.userDetails.isExternal || false
                });
            } else {
                console.warn("No userDetails found in context - MANDATORY DATA MISSING");
            }
        },

        buildWorkspaceData(contextHierarchy: { title: string; value: string }[]) {
            const knownKeys = ["client", "market", "brand", "region", "industry"] as const;
            const workspaceData: Record<string, string | null> = {
                client: null,
                market: null,
                brand: null,
                region: null,
                industry: null,
                custom: null
            };

            for (const item of contextHierarchy) {
                const key = item.title.toLowerCase();
                if (knownKeys.includes(key as (typeof knownKeys)[number])) {
                    workspaceData[key] = item.value;
                } else if (item.title && item.title !== "test") {
                    workspaceData.custom = item.value;
                }
            }

            return workspaceData;
        },

        setGA4ApplicationData(context: typeof this.wppOsContext) {
            if (context.appInstance) {
                this.ga4Tracker.setApplicationData({
                    applicationId: context.appInstance.devhubAppId,
                    applicationInstanceId: context.appInstance.id,
                    productType: context.appInstance.devhubMetadata?.productType || "unknown",
                    nocodeApptype: context.appInstance.devhubMetadata?.noCodeAppType || undefined,
                    productName: "Assemble",
                    productCategory: "Production",
                    productSubcategory: "Content"
                });
            } else {
                console.warn("No appInstance found in context - APPLICATION DATA MISSING");
            }
        },

        async initializeGA4WithOpenOSContext() {
            if (this.ga4Tracker?.isInitialized) {
                console.log("wpp login: GA4 already initialized");
                return;
            }

            const context = this.wppOsContext;

            if (!this.ga4Tracker || !context) {
                console.log("Failed to initialize GA4 with OpenOS context; missing data");
                return;
            }

            if (this.ga4Tracker.isInitializing) {
                return;
            }

            this.ga4Tracker.isInitializing = true;

            try {
                await this.setGA4UserData(context);

                const contextHierarchy = context.project?.contextHierarchy;
                const workspaceData = Array.isArray(contextHierarchy)
                    ? this.buildWorkspaceData(contextHierarchy)
                    : { client: null, market: null, brand: null, region: null, industry: null, custom: null };

                this.ga4Tracker.setWppWorkspaceData({
                    market: workspaceData.market ?? undefined,
                    industry: workspaceData.industry ?? undefined,
                    brand: workspaceData.brand ?? undefined,
                    client: workspaceData.client ?? undefined,
                    region: workspaceData.region ?? undefined,
                    custom: workspaceData.custom ?? undefined
                });

                this.setGA4ApplicationData(context);

                const projectName =
                    context.project.name ?? (this.wppProjectId ? `Project ${this.wppProjectId}` : undefined);

                this.ga4Tracker.setContextData({
                    contextType: "os",
                    project: projectName ?? undefined,
                    tenantName: context.tenant.name || "WPP Open"
                });

                this.ga4Tracker.setInitialized();

                this.ga4Tracker.trackPageView();
                this.ga4Tracker.trackAppLaunch();
            } catch (error) {
                console.error("Failed to initialize GA4 with Open OS context:", error);
                console.error("Context structure:", context);
            }

            this.ga4Tracker.isInitializing = false;
        },

        async initializeGA4WithStandaloneUser() {
            if (this.isWppLogin) {
                return;
            }

            if (this.ga4Tracker?.isInitialized) {
                console.log("standalone user: GA4 already initialized");

                return;
            }

            if (!this.ga4Tracker || !this.me) {
                console.log("Failed to initialize GA4 with standalone user, missing data");

                return;
            }

            if (this.ga4Tracker.isInitializing) {
                return;
            }

            this.ga4Tracker.isInitializing = true;

            try {
                await this.ga4Tracker.setUserData({
                    email: this.me.id,
                    company: undefined,
                    countryCode: undefined,
                    department: undefined,
                    jobRole: undefined,
                    region: undefined,
                    isExternal: false
                });

                this.ga4Tracker.setApplicationData({
                    applicationId: undefined,
                    applicationInstanceId: undefined,
                    productType: "STANDALONE_APPLICATION",
                    productName: "Assemble",
                    productCategory: "Production",
                    productSubcategory: "Content"
                });

                this.ga4Tracker.setInitialized();

                this.ga4Tracker.trackPageView();
                this.ga4Tracker.trackUserLogin();
                this.ga4Tracker.trackAppLaunch();
            } catch (error) {
                console.error("Failed to initialize GA4 with standalone user", error);
            }

            this.ga4Tracker.isInitializing = false;
        }
    },

    apollo: {
        me: {
            query: meQuery,
            pollInterval: 30000,
            skip() {
                return !this.isAuthorized;
            },
            result({ data, error }: { data: { me?: Me }; error?: Error }) {
                if (!error && data.me) {
                    this.$store.commit(AuthMutation.SetMe, { me: data.me });

                    void this.initializeGA4WithStandaloneUser();
                }
            }
        },
        roles: {
            query: rolesQuery,
            skip() {
                return !this.isAuthorized || !this.$store.state.auth.me || !userCan(USER_ACTIONS.canListRoles);
            },
            result({ data, error }: { data: { roles: Role[] }; error?: Error }) {
                if (!error) {
                    void this.$store.dispatch(RolesAction.SetRoles, data.roles);
                }
            }
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;
/////////////////////
// iview overrides
////////////////////
@use "@style/iview-overrides" as *;
/////////////////////
// main hoxton styles
////////////////////
@use "@style/hoxton" as *;

@use "@style/theme.overwrite" as *;
@use "@style/dark.theme";
</style>
